/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { useState } from 'react'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { ArrowProps } from 'react-crane'

import FormLabel from '../FormLabel'
import Dropdown, { DropdownProps, DropdownMode } from '../Dropdown'
import { LabelVisibility } from '../../../../types'
import { captionTextCss } from '../../../../lib/styles/typography'
import { errorTextColor } from '../../../../lib/styles/colors'

const selectContainerCss = css`
  position: relative;
  padding-bottom: 5px;
  margin-left: 8px;
  margin-right: 8px;
`
const openedIconCss = css`
  transform: rotate(180deg);
`

const helperTextCss = css`
  ${captionTextCss}
  margin-top: 6px;
  padding-left: 2px;
`

const helperTextErrorCss = css`
  color: ${errorTextColor};
`

export type SelectProps = {
  /** Indicates field is an error state */
  error?: boolean
  /** Id of control */
  id: string
  /** Optional helper text to display with the dropdown */
  helperText?: any
  /** Label for field */
  label: string
  /** Label key for an optionl */
  labelKey?: string
  /** Label visibility behavior */
  labelVisibility?: LabelVisibility
  /** The type of dropdown, such as Simple or Filter or Multiselect */
  mode?: DropdownMode
  /** Optional callback for dropdown blur */
  onDropdownBlur?: () => void
  /** Optional callback for dropdown focus */
  onDropdownFocus?: () => void
  /** Indicates that label should be screen-ready only */
  srOnlyLabel?: boolean
  /** Value key for an option */
  valueKey?: string
} & DropdownProps

const defaultProps: Pick<
  SelectProps,
  | 'error'
  | 'helperText'
  | 'labelKey'
  | 'labelVisibility'
  | 'mode'
  | 'onDropdownBlur'
  | 'onDropdownFocus'
  | 'srOnlyLabel'
  | 'valueKey'
> = {
  error: false,
  helperText: undefined,
  labelKey: 'label',
  labelVisibility: LabelVisibility.DEFAULT,
  mode: DropdownMode.Filter,
  onDropdownBlur: undefined,
  onDropdownFocus: undefined,
  srOnlyLabel: false,
  valueKey: 'value'
}

const arrowRenderer = ({ isOpen }: ArrowProps) => (
  <ArrowDropDownIcon css={[isOpen && openedIconCss]} />
)

const Select = ({
  error,
  id,
  className,
  helperText,
  label,
  labelKey,
  labelVisibility,
  onDropdownBlur,
  onDropdownFocus,
  placeholder,
  srOnlyLabel,
  value,
  valueKey,
  ...props
}: SelectProps) => {
  const [focused, setFocused] = useState(false)

  const handleFocus = () => {
    setFocused(true)

    if (onDropdownFocus) {
      onDropdownFocus()
    }
  }

  const handleBlur = () => {
    setFocused(false)

    if (onDropdownBlur) {
      onDropdownBlur()
    }
  }

  const labelHiddenOnFilled =
    labelVisibility === LabelVisibility.VISIBLE_ON_FOCUS && value && !focused
  const labelHidden = labelVisibility === LabelVisibility.HIDDEN

  const helperTextSetsCss = [helperTextCss, error && helperTextErrorCss]

  return (
    <div css={selectContainerCss} className={className}>
      <FormLabel
        associatedId={id}
        error={error}
        focused={focused}
        shrink={!!value || focused}
        srOnly={labelHidden || labelHiddenOnFilled || srOnlyLabel || !!(!focused && placeholder)}
      >
        {label}
      </FormLabel>
      <Dropdown
        id={`${id}-dropdown`}
        {...props}
        arrowRenderer={arrowRenderer}
        error={error}
        inputId={id}
        labelKey={labelKey}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        srOnlyLabel={srOnlyLabel}
        value={value}
        valueKey={valueKey}
      />
      {helperText && typeof helperText === 'string' ? (
        <p aria-live={error ? 'polite' : 'off'} css={helperTextSetsCss}>
          {helperText}
        </p>
      ) : (
        helperText
      )}
    </div>
  )
}

Select.defaultProps = defaultProps

export default Select
